import type { ToolMessagePart } from './timeline';

/**
 * Display names for selected sources in CORTEX_WEB_SEARCH_ALLOWED_DOMAINS.
 * This is a naming catalog, not an access policy or proof that a source was read.
 * Unknown domains stay generic; adding a backend domain never grants it a UI label.
 */
const SOURCE_NAMES: Readonly<Record<string, string>> = {
  'en.wikipedia.org': 'Wikipedia',
  'mayoclinic.org': 'Mayo Clinic',
  'cdc.gov': 'CDC',
  'fda.gov': 'FDA',
  'nih.gov': 'NIH',
  'nlm.nih.gov': 'NLM',
  'pubmed.ncbi.nlm.nih.gov': 'PubMed',
  'ncbi.nlm.nih.gov': 'NCBI',
  'medlineplus.gov': 'MedlinePlus',
  'cancer.gov': 'NCI',
  'cms.gov': 'CMS',
  'hhs.gov': 'HHS',
  'who.int': 'WHO',
  'nhs.uk': 'NHS',
  'nice.org.uk': 'NICE',
  'ema.europa.eu': 'EMA',
  'ecdc.europa.eu': 'ECDC',
  'uspreventiveservicestaskforce.org': 'USPSTF',
  'ahrq.gov': 'AHRQ',
  'aafp.org': 'AAFP',
  'aap.org': 'AAP',
  'acponline.org': 'ACP',
  'acpjournals.org': 'ACP Journals',
  'acog.org': 'ACOG',
  'acc.org': 'ACC',
  'jacc.org': 'JACC',
  'heart.org': 'AHA',
  'ahajournals.org': 'AHA Journals',
  'thoracic.org': 'ATS',
  'journal.chestnet.org': 'CHEST',
  'acep.org': 'ACEP',
  'idsociety.org': 'IDSA',
  'asco.org': 'ASCO',
  'nccn.org': 'NCCN',
  'endocrine.org': 'Endocrine Society',
  'gastro.org': 'AGA',
  'rheumatology.org': 'American College of Rheumatology',
  'acr.org': 'American College of Radiology',
  'psychiatry.org': 'APA',
  'diabetes.org': 'ADA',
  'kidney.org': 'NKF',
  'aan.com': 'AAN',
  'escardio.org': 'ESC',
  'esmo.org': 'ESMO',
  'nejm.org': 'NEJM',
  'jamanetwork.com': 'JAMA',
  'thelancet.com': 'The Lancet',
  'bmj.com': 'BMJ',
  'cmaj.ca': 'CMAJ',
  'cochrane.org': 'Cochrane',
  'cochranelibrary.com': 'Cochrane',
  'merckmanuals.com': 'Merck Manuals',
  'clinicaltrials.gov': 'ClinicalTrials.gov',
  'my.clevelandclinic.org': 'Cleveland Clinic',
  'hopkinsmedicine.org': 'Johns Hopkins',
  'stanfordhealthcare.org': 'Stanford Health Care',
  'health.harvard.edu': 'Harvard Health',
  'clinicalinfo.hiv.gov': 'HIV.gov',
};

// Deliberately recognize only simple positive site filters. Quoted expressions,
// negation and boolean queries have ambiguous scope and keep a generic label.
const SITE_OPERATOR = /(?:^|\s)site:([a-z0-9.-]+)(?:\/[^\s]*)?(?=\s|$)/gi;
const AMBIGUOUS_QUERY = /["'()|]|\b(?:OR|NOT)\b|(?:^|\s)-/i;

function sourceName(hostname: string): string | null {
  return SOURCE_NAMES[hostname.toLowerCase().replace(/^www\./, '')] ?? null;
}

function urlSourceName(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null;
  }
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' || url.protocol === 'http:' ? sourceName(url.hostname) : null;
  } catch {
    return null;
  }
}

function inputOf(part: ToolMessagePart): Record<string, unknown> {
  return typeof part.input === 'object' && part.input !== null
    ? (part.input as Record<string, unknown>)
    : {};
}

function queriesOf(part: ToolMessagePart): string[] | null {
  const { type, query, queries } = inputOf(part);
  if (type !== undefined && type !== 'search') {
    return null;
  }
  if (query !== undefined && typeof query !== 'string') {
    return null;
  }
  if (
    queries !== undefined &&
    (!Array.isArray(queries) || queries.some((q) => typeof q !== 'string'))
  ) {
    return null;
  }
  const values: string[] = [
    typeof query === 'string' ? query : '',
    ...(Array.isArray(queries) ? queries : []),
  ];
  return values.filter((value) => value.trim());
}

function searchedSources(parts: ToolMessagePart[]): { names: string[]; searchedWider: boolean } {
  const names = new Set<string>();
  let searchedWider = false;
  for (const part of parts) {
    const queries = queriesOf(part);
    if (!queries?.length) {
      searchedWider = true;
    }
    for (const query of queries ?? []) {
      if (AMBIGUOUS_QUERY.test(query)) {
        searchedWider = true;
        continue;
      }
      const sites = [...query.matchAll(SITE_OPERATOR)].map((match) => match[1]!);
      if (!sites.length) {
        searchedWider = true;
      }
      for (const site of sites) {
        const name = sourceName(site);
        if (name) {
          names.add(name);
        } else {
          searchedWider = true;
        }
      }
    }
  }
  return { names: [...names], searchedWider };
}

function formatSourceList(names: string[], andTheWeb = false): string {
  const items = names.length > 4 ? [...names.slice(0, 3), `${names.length - 3} more`] : [...names];
  if (andTheWeb) {
    items.push('the web');
  }
  if (items.length <= 1) {
    return items[0] ?? '';
  }
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

/** Receives only live or successful calls; completion and failure handling lives in timeline. */
export function getWebActivityLabel(
  toolName: string,
  parts: ToolMessagePart[],
  isRunning: boolean
): string | null {
  if (toolName === 'web_search') {
    // Native search also emits open_page and find_in_page actions. Do not turn
    // those into searches, or silently assign them another call's site filters.
    const actions = new Set(parts.map((part) => inputOf(part).type ?? 'search'));
    if (actions.size !== 1 || !actions.has('search')) {
      return isRunning ? 'Browsing the web' : 'Browsed the web';
    }
    const { names, searchedWider } = searchedSources(parts);
    if (!names.length) {
      return null;
    }
    const sources = formatSourceList(names, searchedWider);
    return isRunning ? `Searching ${sources}` : `Searched ${sources}`;
  }
  if (toolName === 'web_fetch') {
    const names = parts.map((part) => urlSourceName(inputOf(part).url));
    if (names.some((name) => name === null)) {
      return null;
    }
    const sources = formatSourceList([
      ...new Set(names.filter((name): name is string => name !== null)),
    ]);
    return isRunning ? `Opening ${sources}` : `Opened ${sources}`;
  }
  return null;
}
